import { useEffect, useState } from 'react'
import { loadKey, saveKey, subscribeKey, uid } from './index'
import { STRATEGIES, OFFICES, ALL_SIPPS, SITES } from './catalog'

const KEY = 'activityLog'

/**
 * Activity log — every hit the repricing engine handles, whatever the
 * outcome. Replaces the standalone "Blocked repricings" queue by
 * absorbing it as one event_type among many.
 *
 * Event types (extensible):
 *   - no_strategy      → no strategy matched the incoming rate shop
 *   - no_price_change  → strategy matched but the delta was 0 (or below
 *                        the threshold), nothing to push
 *   - blocked_by_limit → strategy proposed a change but a price limit
 *                        or consistency rule stopped it (the old queue)
 *   - applied          → change pushed to the PMS successfully — the
 *                        row carries every API request/response
 *   - pms_error        → the PMS rejected the push
 *
 * Each row also carries a `resolution` field for actionable types
 * (blocked_by_limit → 'pending' | 'approved' | 'discarded'; others → 'n/a').
 */

const EVENT_TYPES = [
	'no_strategy','no_rate_card','no_price_change','capped','quarantined','applied','tethered',
	'pms_error','tethered_pms_error',
	// Not a decision the engine took — an edit somebody made to the
	// configuration. It shares the feed so the effect of a change is
	// visible next to the change: edit a strategy, see the repricing
	// that follows it (Curro, 2026-09-28).
	'setup_change',
	// Written by the flush, not by a decision (CU-869fb9wz6).
	'ladder_drift', 'ladder_conflict',
]

// Legacy → current event-type map. Rows written under the old
// 'blocked_by_limit' name are surfaced as 'capped' in the UI without
// any data migration required at read time.
const LEGACY_EVENT_TYPE_ALIAS = { blocked_by_limit: 'capped' }
export function canonicalEventType(t) { return LEGACY_EVENT_TYPE_ALIAS[t] || t }

const RATE_SHOP_SOURCES = ['rentalcars.com','carwiz','discovercars','cartrawler','sunnycars','own_web']

function pick(arr) { return arr[Math.floor(Math.random() * arr.length)] }
function jitter(base, spread) { return Math.round((base + (Math.random() - 0.5) * spread) * 100) / 100 }
function dateISO(d) { return d.toISOString().slice(0, 10) }

function seed() {
	const now = Date.now()
	const rows = []

	// 34 capped events on a shared price_limit — the "one limit caps
	// many hits" pattern that operators want to spot at a glance so
	// they can override the limit and re-release them.
	for (let i = 0; i < 34; i++) {
		rows.push(makeCapped({
			ts: now - i * 45 * 60 * 1000,
			office: 'LPA-AP', sipp: 'CDMR',
			strategy: STRATEGIES[0],
			proposed: jitter(21.5, 2), limit_value: 25,
			limit_id: 'plim-shared-1',
			limit_label: 'Price limit · LPA-AP · CDMR · 4-7d · min 25€',
			limit_kind: 'price_limit',
		}))
	}
	for (let i = 0; i < 12; i++) {
		rows.push(makeCapped({
			ts: now - (5 + i) * 60 * 60 * 1000,
			office: 'TFS-AP', sipp: 'EDMR',
			strategy: STRATEGIES[1],
			proposed: jitter(17.5, 2), limit_value: 20,
			limit_id: 'plim-tfs-edmr',
			limit_label: 'Price limit · TFS-AP · EDMR · 1-3d · min 20€',
			limit_kind: 'price_limit',
		}))
	}
	for (let i = 0; i < 8; i++) {
		rows.push(makeCapped({
			ts: now - (30 + i * 20) * 60 * 1000,
			office: 'LPA-AP', sipp: 'MDMR',
			strategy: STRATEGIES[2],
			proposed: jitter(32.5, 1), limit_value: 28,
			limit_id: 'cons-mdmr-edmr',
			limit_label: 'Consistency · MDMR ≤ EDMR (min delta 2€)',
			limit_kind: 'consistency_rule',
		}))
	}

	// ~7 quarantined events — dry-run: the engine did the full
	// evaluation but the strategy is flagged read-only, so no push.
	for (let i = 0; i < 7; i++) {
		rows.push(makeQuarantined({
			ts: now - (1 + i * 3) * 60 * 60 * 1000,
			office: pick(OFFICES).code,
			sipp: pick(ALL_SIPPS),
			strategy: pick(STRATEGIES),
			proposed: jitter(30, 15),
			current_price: jitter(28, 15),
		}))
	}

	// ~45 applied (with fake PMS API request/response). The healthy path.
	for (let i = 0; i < 45; i++) {
		const office = pick(OFFICES).code
		const sipp   = pick(ALL_SIPPS)
		const prev   = jitter(35, 20)
		const next   = jitter(prev, 10)
		rows.push(makeApplied({
			ts: now - i * 22 * 60 * 1000,
			office, sipp,
			strategy: pick(STRATEGIES),
			previous_price: prev,
			new_price: next,
			writes: fakeWrites(office, sipp, next),
		}))
	}

	// ~10 no_price_change — same price after evaluation, nothing to do.
	for (let i = 0; i < 10; i++) {
		rows.push(makeNoChange({
			ts: now - (2 + i * 3) * 60 * 60 * 1000,
			office: pick(OFFICES).code,
			sipp: pick(ALL_SIPPS),
			strategy: pick(STRATEGIES),
			current_price: jitter(28, 15),
		}))
	}

	// ~15 no_strategy — the rate shop matched no strategy at all.
	for (let i = 0; i < 15; i++) {
		rows.push(makeNoStrategy({
			ts: now - (1 + i * 2) * 60 * 60 * 1000,
			office: pick(OFFICES).code,
			sipp: pick(ALL_SIPPS),
		}))
	}

	// ~12 tethered — child prices inferred from a parent applied via a
	// tethering rule. Anchored to an existing applied row so the trace
	// (parent_event_id) is real.
	const parentAppliedPool = rows.filter(r => r.event_type === 'applied').slice(0, 20)
	const sampleTethers = [
		{ id: 'seed-t1', name: 'ACRISS: MDMR → MDAR', dimension: 'acriss',
		  parent: 'MDMR', delta: { k: 'pct', v: 12 } },
		{ id: 'seed-t2', name: 'LOR: 1 day → 3 days, 5 days', dimension: 'lor',
		  parent: '1',   delta: { k: 'pct', v: 5 } },
		{ id: 'seed-t3', name: 'Office: LPA-AP → LPA-CEN', dimension: 'office',
		  parent: 'LPA-AP', delta: { k: 'abs', v: 3 } },
	]
	for (let i = 0; i < 12 && parentAppliedPool.length > 0; i++) {
		const parent  = pick(parentAppliedPool)
		const tether  = pick(sampleTethers)
		const child   = tether.dimension === 'acriss' ? 'MDAR'
		              : tether.dimension === 'lor'    ? pick(['3', '5'])
		              : 'LPA-CEN'
		rows.push(makeTethered({
			ts: parent.ts + 500,   // just after the parent
			parent_row: parent,
			tether,
			child_dim_value: child,
		}))
	}

	// ~3 tethered_pms_error — the child push failed even though the
	// parent applied succeeded. Especially bad: leaves parent+child
	// out of sync and needs manual reconciliation.
	for (let i = 0; i < 3 && parentAppliedPool.length > 0; i++) {
		const parent = parentAppliedPool[i]
		const tether = sampleTethers[i % sampleTethers.length]
		const child  = tether.dimension === 'acriss' ? 'MDAR'
		             : tether.dimension === 'lor'    ? '5'
		             : 'LPA-CEN'
		rows.push(makeTethered({
			ts: parent.ts + 900,
			parent_row: parent,
			tether,
			child_dim_value: child,
			fail: true,
		}))
	}

	// ~6 pms_error — the applied push failed at the API layer.
	for (let i = 0; i < 6; i++) {
		const office = pick(OFFICES).code
		const sipp   = pick(ALL_SIPPS)
		const next   = jitter(38, 15)
		rows.push(makePmsError({
			ts: now - (8 + i * 4) * 60 * 60 * 1000,
			office, sipp,
			strategy: pick(STRATEGIES),
			new_price: next,
			writes: fakeWrites(office, sipp, next).map((w, idx) =>
				idx === 0
					? { ...w, status: 'error',
					    response: { code: 502, body: '{"error":"Upstream timeout"}' } }
					: w,
			),
			error: { code: 502, api: 'Ranteon /api/ExPricelist/UpdatePrice',
			         message: 'Upstream timeout after 5s' },
		}))
	}

	// Newest first.
	return rows.sort((a, b) => b.ts - a.ts)
}

// ---------------------------------------------------------------------------
// Row factories
// ---------------------------------------------------------------------------
const commonScope = ({ office, sipp, days = 4 }) => {
	const d = new Date(); d.setDate(d.getDate() + Math.floor(Math.random() * 60))
	// search_id follows the same shape Real-Time Searches uses in its
	// idSearch query param — a compact UID so the deep-link "/real-time/
	// searches?idSearch=..." works out of the box against the mock.
	return { office, sipp, duration_days: days || pick([1, 3, 5, 7, 14]),
	         pickup_date: dateISO(d), source: pick(RATE_SHOP_SOURCES),
	         search_id: uid() }
}

// A "capped" event: the engine wanted to push `proposed` but the
// price limit forced it up (or down) to `limit_value`. The change WAS
// applied — this row is not a block, it's an attenuation. `capped_to`
// is the price actually written; `proposed_price` is what the strategy
// wanted before the cap.
function makeCapped({ ts, office, sipp, strategy, proposed, limit_value,
                     limit_id, limit_label, limit_kind }) {
	// Floor semantics: pushed = max(proposed, limit_value).
	const capped_to = Math.max(proposed, limit_value)
	return {
		id: uid(), ts,
		event_type: 'capped',
		scope: commonScope({ office, sipp }),
		strategy_id: strategy.id, strategy_name: strategy.name,
		proposed_price: proposed,
		capped_to,
		limit: { id: limit_id, label: limit_label, kind: limit_kind,
		         value: limit_value },
		resolution: 'capped',   // capped in place (no further action needed)
	}
}
// Backwards-compat alias so older callers keep working during the rename.
const makeBlocked = makeCapped

// A "quarantined" event: the engine evaluated everything (strategy
// matched, price proposed) but the rule is in dry-run / read-only
// mode, so nothing was pushed. Waiting for a human to apply or discard.
function makeQuarantined({ ts, office, sipp, strategy, proposed, current_price }) {
	return {
		id: uid(), ts,
		event_type: 'quarantined',
		scope: commonScope({ office, sipp }),
		strategy_id: strategy.id, strategy_name: strategy.name,
		current_price, proposed_price: proposed,
		delta: Math.round((proposed - current_price) * 100) / 100,
		resolution: 'pending',
	}
}
function makeApplied({ ts, office, sipp, strategy, previous_price, new_price, writes }) {
	return {
		id: uid(), ts,
		event_type: 'applied',
		scope: commonScope({ office, sipp }),
		strategy_id: strategy.id, strategy_name: strategy.name,
		previous_price, new_price,
		delta: Math.round((new_price - previous_price) * 100) / 100,
		writes,
		resolution: 'n/a',
	}
}

// A "tethered" event: the child price was NOT computed by a strategy —
// it was derived from a parent applied/tethered event via a tethering
// rule (parent_price × (1+%) or + €). Full trace:
//   strategy → parent applied → tether → this row
// Every field mirrors `applied` so the row plugs into the same UI
// affordances (writes JSON, delta, resolution) with extra provenance.
//
// If `fail` is truthy, this becomes a `tethered_pms_error` — the child
// push failed even though the parent applied succeeded, leaving the
// system in an inconsistent state that a human must reconcile.
function makeTethered({ ts, parent_row, tether, child_dim_value, fail = false }) {
	const parent_price = parent_row.new_price ?? parent_row.capped_to ?? 0
	const derived = tether.delta.k === 'pct'
		? parent_price * (1 + (tether.delta.v || 0) / 100)
		: parent_price + (tether.delta.v || 0)
	const new_price = Math.round(derived * 100) / 100
	// Guess a plausible previous_price the child used to have.
	const previous_price = Math.round((new_price + (Math.random() - 0.5) * 6) * 100) / 100
	// The child's scope tweaks whichever dimension the tether targets;
	// other dims copy from the parent for realism.
	const childScope = { ...parent_row.scope }
	if (tether.dimension === 'acriss')   childScope.sipp = child_dim_value
	if (tether.dimension === 'office')   childScope.office = child_dim_value
	if (tether.dimension === 'lor')      childScope.duration_days = Number(child_dim_value)
	// (category not stored on scope directly — leave scope alone)
	const baseWrites = fakeWrites(childScope.office || 'LPA-AP',
	                              childScope.sipp   || 'CDMR',
	                              new_price)
	const writesForFailure = baseWrites.map((w, idx) =>
		idx === 0
			? { ...w, status: 'error',
			    response: { code: 502, body: '{"error":"Upstream timeout"}' } }
			: w,
	)
	return {
		id: uid(), ts,
		event_type: fail ? 'tethered_pms_error' : 'tethered',
		scope: { ...childScope, search_id: uid() },
		strategy_id: parent_row.strategy_id,
		strategy_name: parent_row.strategy_name,
		previous_price, new_price,
		delta: Math.round((new_price - previous_price) * 100) / 100,
		writes: fail ? writesForFailure : baseWrites,
		error: fail
			? { code: 502, api: 'Ranteon /api/ExPricelist/UpdatePrice',
			    message: 'Upstream timeout — child push failed after parent applied. System inconsistent.' }
			: undefined,
		resolution: fail ? 'pending' : 'n/a',
		via_tether: {
			tether_id:   tether.id,
			tether_name: tether.name,
			dimension:   tether.dimension,
			delta:       tether.delta,
			parent_value: tether.parent,
			child_value:  child_dim_value,
			parent_event_id: parent_row.id,
			parent_price,
			// Snapshot of the parent event so the trace is autonomous —
			// even if the parent gets purged later, the tethered row
			// keeps a full record of what triggered it.
			parent_snapshot: {
				ts:              parent_row.ts,
				event_type:      parent_row.event_type,
				scope:           parent_row.scope,
				strategy_id:     parent_row.strategy_id,
				strategy_name:   parent_row.strategy_name,
				previous_price:  parent_row.previous_price,
				new_price:       parent_row.new_price,
				writes_count:    (parent_row.writes || []).length,
			},
		},
	}
}
function makeNoChange({ ts, office, sipp, strategy, current_price }) {
	return {
		id: uid(), ts,
		event_type: 'no_price_change',
		scope: commonScope({ office, sipp }),
		strategy_id: strategy.id, strategy_name: strategy.name,
		current_price, new_price: current_price,
		reason: 'Proposed price equals current — no push required.',
		resolution: 'n/a',
	}
}
function makeNoStrategy({ ts, office, sipp }) {
	return {
		id: uid(), ts,
		event_type: 'no_strategy',
		scope: commonScope({ office, sipp }),
		reason: 'No strategy matched (office / ACRISS / date fell outside every rule).',
		resolution: 'n/a',
	}
}
function makePmsError({ ts, office, sipp, strategy, new_price, writes, error }) {
	return {
		id: uid(), ts,
		event_type: 'pms_error',
		scope: commonScope({ office, sipp }),
		strategy_id: strategy.id, strategy_name: strategy.name,
		new_price,
		writes,
		error,
		resolution: 'n/a',
	}
}

function fakeWrites(office, sipp, price) {
	// 1-3 write pricelists, each with request/response payload.
	const sites = pick(SITES)
	const nWrites = 1 + Math.floor(Math.random() * 3)
	const targets = ['WEB-PR','Discover Prepaid','Cartrawler Prepaid','Rentalcars Prepaid',
	                 'SC-PP','Rentalcar Genius Pr'].slice(0, nWrites)
	return targets.map(pl => ({
		pricelist: pl,
		api: `Ranteon /api/ExPricelist/UpdatePrice/${pl.replace(/\s+/g,'-')}`,
		request:  { pricelist: pl, office, sipp, price,
		            ts: new Date().toISOString() },
		response: { code: 200, body: '{"ok":true}' },
		status:   'ok',
	}))
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------
export function loadActivity() {
	const existing = loadKey(KEY, null)
	if (existing) return existing
	const seeded = seed()
	saveKey(KEY, seeded)
	return seeded
}

export function saveActivity(rows) { saveKey(KEY, rows) }

export function useActivity() {
	const [rows, setRows] = useState(() => loadActivity())
	useEffect(() => subscribeKey(KEY, setRows), [])
	return rows
}

// Legacy sidebar-badge helper: now counts currently-capped rows (the
// operationally-actionable subset — override the limit and they release).
export function useBlockedCount() {
	const rows = useActivity()
	return rows.filter(r => (r.event_type === 'capped'
	                          || r.event_type === 'blocked_by_limit')
	                     && r.resolution === 'capped').length
}
export function useCappedCount() { return useBlockedCount() }
export function useQuarantinedCount() {
	const rows = useActivity()
	return rows.filter(r => r.event_type === 'quarantined'
	                     && r.resolution === 'pending').length
}
export function useTetheredErrorCount() {
	const rows = useActivity()
	return rows.filter(r => r.event_type === 'tethered_pms_error'
	                     && r.resolution === 'pending').length
}

// Retry a failed push (either plain pms_error or tethered_pms_error).
// In the mock this flips the row into a successful state and emits a
// synthetic Applied / Tethered event so the trace stays consistent.
export function retryPmsErrorRows(ids) {
	const now = Date.now()
	const affected = loadActivity().filter(r => ids.includes(r.id)
	                                          && (r.event_type === 'pms_error'
	                                               || r.event_type === 'tethered_pms_error')
	                                          && (r.resolution === 'pending'
	                                               || r.resolution == null
	                                               || r.resolution === 'n/a'))
	const newRetries = affected.map(r => ({
		id: uid(), ts: now,
		event_type: r.event_type === 'tethered_pms_error' ? 'tethered' : 'applied',
		scope: r.scope,
		strategy_id: r.strategy_id, strategy_name: r.strategy_name,
		previous_price: r.previous_price,
		new_price: r.new_price,
		delta: r.delta,
		writes: (r.writes || []).map(w => ({ ...w, status: 'ok',
		                                     response: { code: 200, body: '{"ok":true}' } })),
		resolution: 'n/a',
		...(r.via_tether ? { via_tether: r.via_tether } : {}),
		note: `Retry of failed event ${r.id.slice(0, 6)}`,
	}))
	updateRows(rows => {
		const patched = rows.map(r =>
			ids.includes(r.id) && (r.event_type === 'pms_error'
			                        || r.event_type === 'tethered_pms_error')
				? { ...r, resolution: 'retried', resolved_at: now }
				: r,
		)
		return [...newRetries, ...patched]
	})
}
export function ackPmsErrorRows(ids) {
	// Mark as acknowledged (human aware, not retrying) — leaves the
	// system in whatever state it's in but stops it screaming.
	return updateRows(rows => rows.map(r =>
		ids.includes(r.id) && (r.event_type === 'pms_error'
		                        || r.event_type === 'tethered_pms_error')
			? { ...r, resolution: 'acknowledged', resolved_at: Date.now() }
			: r,
	))
}

export function updateRows(mutator) {
	const next = mutator(loadActivity().map(r => ({ ...r })))
	saveActivity(next)
	return next
}
// The capped-row actions (override, override limit) and the quarantine
// ones (apply manually, discard) left this mock on 2026-10-10: they
// have a backend now (`/activity/<id>/override`, `/approve`, `/discard`)
// and a mock that pretended to resolve rows was how a button could
// promise an action that never happened (CU-869fdg4nu).

export const ACTIVITY_EVENT_TYPES = EVENT_TYPES

// Live streaming — every N ms the mock synthesises a new event as if
// the engine had just processed a fresh rate shop. Weighted so most
// hits are healthy (applied / no-op) and blocks/errors are rarer.
const STREAM_WEIGHTS = [
	['applied',              44],
	['no_price_change',      18],
	['no_strategy',          12],
	['tethered',              8],
	['capped',                8],
	['quarantined',           4],
	['pms_error',             4],
	['tethered_pms_error',    2],   // rare but critical — highlighted in UI
]
function weightedPick() {
	const total = STREAM_WEIGHTS.reduce((s, [, w]) => s + w, 0)
	let n = Math.random() * total
	for (const [t, w] of STREAM_WEIGHTS) {
		n -= w
		if (n <= 0) return t
	}
	return STREAM_WEIGHTS[0][0]
}

export function pushRandomEvent() {
	const ts     = Date.now()
	const office = pick(OFFICES).code
	const sipp   = pick(ALL_SIPPS)
	const strat  = pick(STRATEGIES)
	const type   = weightedPick()

	let row
	if (type === 'applied') {
		const prev = jitter(35, 20)
		const next = jitter(prev, 10)
		row = makeApplied({ ts, office, sipp, strategy: strat,
		                    previous_price: prev, new_price: next,
		                    writes: fakeWrites(office, sipp, next) })
	} else if (type === 'no_price_change') {
		row = makeNoChange({ ts, office, sipp, strategy: strat,
		                     current_price: jitter(28, 15) })
	} else if (type === 'no_strategy') {
		row = makeNoStrategy({ ts, office, sipp })
	} else if (type === 'capped') {
		const proposed = jitter(22, 4)
		row = makeCapped({
			ts, office, sipp, strategy: strat,
			proposed, limit_value: proposed + jitter(3, 2),
			limit_id: 'plim-live-' + office,
			limit_label: `Price limit · ${office} · ${sipp} · min ${(proposed + 3).toFixed(2)}€`,
			limit_kind: 'price_limit',
		})
	} else if (type === 'quarantined') {
		const current = jitter(28, 15)
		row = makeQuarantined({
			ts, office, sipp, strategy: strat,
			proposed: jitter(current, 8), current_price: current,
		})
	} else if (type === 'tethered' || type === 'tethered_pms_error') {
		// Piggy-back the most recent applied row if we have one, so the
		// parent_event trace is real. Fall back to synthesising both.
		const recent = loadActivity().find(r => r.event_type === 'applied')
		const parent = recent || {
			id: uid(), ts: ts - 800,
			event_type: 'applied',
			scope: commonScope({ office, sipp }),
			strategy_id: strat.id, strategy_name: strat.name,
			new_price: jitter(35, 10), previous_price: jitter(32, 10),
			writes: fakeWrites(office, sipp, jitter(35, 10)),
		}
		const tether = pick([
			{ id: 'live-t-acriss', name: 'ACRISS: MDMR → MDAR', dimension: 'acriss',
			  parent: 'MDMR', delta: { k: 'pct', v: 12 } },
			{ id: 'live-t-lor',    name: 'LOR: 1 day → 3 days',  dimension: 'lor',
			  parent: '1',    delta: { k: 'pct', v: 5 } },
			{ id: 'live-t-office', name: 'Office: LPA-AP → LPA-CEN', dimension: 'office',
			  parent: 'LPA-AP', delta: { k: 'abs', v: 3 } },
		])
		const child = tether.dimension === 'acriss' ? 'MDAR'
		            : tether.dimension === 'lor'    ? '3'
		            : 'LPA-CEN'
		row = makeTethered({ ts, parent_row: parent, tether, child_dim_value: child,
		                     fail: type === 'tethered_pms_error' })
	} else {
		const next = jitter(38, 15)
		row = makePmsError({
			ts, office, sipp, strategy: strat, new_price: next,
			writes: fakeWrites(office, sipp, next).map((w, idx) =>
				idx === 0
					? { ...w, status: 'error',
					    response: { code: 502, body: '{"error":"Upstream timeout"}' } }
					: w,
			),
			error: { code: 502, api: 'Ranteon /api/ExPricelist/UpdatePrice',
			         message: 'Upstream timeout after 5s' },
		})
	}
	return updateRows(rows => [row, ...rows])
}
